import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronRight, Plus } from 'lucide-react';
import { CLUB_ROLE_DESCRIPTIONS, CLUB_ROLE_LABELS, type ClubDetail, type ClubInvitePreview, type ClubStatus, type ClubSummary, type SessionState } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { useMe, useSession, useSetSession } from '../lib/session';
import { AuthLayout } from '../components/AuthLayout';
import { NewPasswordField } from '../components/PasswordField';
import { useAvailability } from '../lib/availability';
import { Alert, Button, Field, LoadingBlock, PageHeader } from '../components/ui';
import { StateSelect } from './admin/AdminClubsPage';

export const CLUB_STATUS: Record<ClubStatus, { label: string; cls: string }> = {
  pending: { label: 'Waiting for Hoopify', cls: 'bg-warn-soft text-warn' },
  approved: { label: 'Approved', cls: 'bg-ok-soft text-ok' },
  suspended: { label: 'Paused by Hoopify', cls: 'bg-danger-soft text-danger' },
  rejected: { label: 'Not approved', cls: 'bg-sunken text-muted' },
};

export function ClubStatusBadge({ status }: { status: ClubStatus }) {
  return <span className={`inline-flex rounded px-1.5 py-0.5 text-[12px] font-medium ${CLUB_STATUS[status].cls}`}>{CLUB_STATUS[status].label}</span>;
}

/** Your clubs. With just one, go straight to it. */
export function ClubsPage() {
  const clubs = useQuery({ queryKey: ['clubs'], queryFn: () => api.get<ClubSummary[]>('/clubs') });
  if (clubs.isLoading) return <LoadingBlock />;
  if (clubs.isError) return <Alert>{errorMessage(clubs.error)}</Alert>;
  const list = clubs.data ?? [];
  if (list.length === 1) return <Navigate to={`/clubs/${list[0]!.id}`} replace />;
  const register = (
    <Link to="/clubs/register" className="btn-secondary">
      <Plus className="size-4" aria-hidden /> Register a club
    </Link>
  );
  return (
    <>
      <PageHeader title={list.length ? 'Clubs' : 'Club'} description="Clubs set up their teams, coaches and players in one place, and choose when everyone is invited." actions={list.length ? register : undefined} />
      {!list.length ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="rounded-lg border border-line bg-surface p-6">
            <Building2 className="size-8 text-action" aria-hidden />
            <h2 className="mt-3 text-[20px] font-semibold">Run your club on Hoopify</h2>
            <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[14px] text-muted">
              <li>Set up every team, coach and player for the season in one place</li>
              <li>Teams stay as drafts: nobody is emailed until you send the invites, now or on a date</li>
              <li>One player list, with parents’ emails entered once, and next season copied in a click</li>
              <li>PlayHQ fixtures, results and ladders, if your association gives you a key</li>
            </ul>
            <p className="mt-3 text-[14px] text-muted">Hoopify checks each club with a quick phone call before it’s switched on.</p>
            <Link to="/clubs/register" className="btn-primary mt-5">
              <Plus className="size-4" aria-hidden /> Register a club
            </Link>
          </div>
          <div className="self-start rounded-lg border border-dashed border-line-strong p-6 text-[14px]">
            <p className="font-medium">Is your club already on Hoopify?</p>
            <p className="mt-1 text-muted">Ask one of its club admins to invite you. You’ll get an email, and the club shows up here once you accept.</p>
            <p className="mt-4 font-medium">Just coaching a team in a club?</p>
            <p className="mt-1 text-muted">You don’t need this page. The club adds your team, or invites you to add it, and you’ll see it on your Teams page.</p>
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
          {list.map((c) => (
            <li key={c.id}>
              <Link to={`/clubs/${c.id}`} className="flex items-center gap-4 px-4 py-3.5 hover:bg-sunken sm:px-5">
                <Building2 className="size-5 shrink-0 text-muted" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                    <span className="font-medium">{c.name}</span>
                    {c.status !== 'approved' && <ClubStatusBadge status={c.status} />}
                  </div>
                  <p className="mt-0.5 text-[13px] text-muted">{[CLUB_ROLE_LABELS[c.role], [c.suburb, c.state].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</p>
                </div>
                <ChevronRight className="size-4 shrink-0 text-faint" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export function RegisterClubPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [v, setV] = useState({ name: '', mobile: '', suburb: '', state: '', website: '' });
  const save = useMutation({
    mutationFn: () => api.post<ClubDetail>('/clubs', { ...v, state: v.state || null }),
    onSuccess: async (c) => {
      await qc.invalidateQueries({ queryKey: ['clubs'] });
      navigate(`/clubs/${c.id}`, { replace: true });
    },
  });
  const f = fieldErrors(save.error);
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  return (
    <>
      <PageHeader title="Register your club" description="Set up all your club’s teams, coaches and players in one place. Hoopify checks each club before it can be used." />
      <div className="grid gap-8 lg:grid-cols-[minmax(0,520px)_1fr]">
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="space-y-4" noValidate>
          {!me.emailVerified && <Alert tone="warn">Confirm your email address first: check your inbox for the link.</Alert>}
          {save.isError && !Object.keys(f).length && <Alert>{errorMessage(save.error)}</Alert>}
          <Field label="Club name" value={v.name} onChange={set('name')} error={f.name} placeholder="e.g. Knox Pumas Basketball Club" autoFocus />
          <Field
            label="Your mobile"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={v.mobile}
            onChange={set('mobile')}
            error={f.mobile}
            placeholder="0412 345 678"
            hint="We’ll call you on this to check it’s your club. It isn’t shown to anyone else."
          />
          <div className="grid grid-cols-[1fr_auto] gap-4">
            <Field label="Suburb (optional)" value={v.suburb} onChange={set('suburb')} error={f.suburb} />
            <StateSelect value={v.state} onChange={(s) => setV({ ...v, state: s })} />
          </div>
          <Field label="Club website (optional)" value={v.website} onChange={set('website')} error={f.website} placeholder="e.g. knoxpumas.com.au" />
          <p className="text-[14px] text-muted">
            You’ll be the club admin, as <strong className="font-semibold text-ink">{me.name}</strong> ({me.email}).
          </p>
          <Button type="submit" loading={save.isPending} disabled={!me.emailVerified}>
            Register club
          </Button>
        </form>
        <aside className="self-start rounded-lg border border-line bg-surface p-5 text-[14px]">
          <p className="font-semibold">What happens next</p>
          <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-muted">
            <li>Hoopify calls you on your mobile, usually within a day or two.</li>
            <li>Once your club is approved, you set up seasons, teams and your player list.</li>
            <li>Teams start as drafts. Nobody is emailed until you choose to send the invites, now or on a date.</li>
          </ol>
        </aside>
      </div>
    </>
  );
}

/** Accepting an invite to help run a club. */
export function ClubInvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useSetSession();
  const { data: session } = useSession();
  const invite = useQuery({ queryKey: ['club-invite', token], queryFn: () => api.get<ClubInvitePreview>(`/club-invites/${token}`) });
  const accept = useMutation({
    mutationFn: () => api.post<{ clubId: string }>(`/club-invites/${token}/accept`),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      navigate(`/clubs/${r.clubId}`, { replace: true });
    },
  });
  const signOut = useMutation({ mutationFn: () => api.post<SessionState>('/auth/logout'), onSuccess: setSession });

  if (invite.isLoading) {
    return (
      <AuthLayout title="Opening your invite…">
        <LoadingBlock />
      </AuthLayout>
    );
  }
  if (invite.isError || !invite.data) {
    return (
      <AuthLayout title="This invite can’t be used">
        <Alert>{errorMessage(invite.error)}</Alert>
        <Link to="/" className="btn-secondary mt-5 w-full">
          Go to Hoopify
        </Link>
      </AuthLayout>
    );
  }
  const inv = invite.data;
  const here = `/club-invite/${token}`;
  const user = session?.status === 'signed_in' ? session.user : null;
  return (
    <AuthLayout
      title={`Help run ${inv.clubName}`}
      description={
        <>
          {inv.invitedBy} invited <strong className="font-semibold text-ink">{inv.email}</strong> to be {inv.role === 'admin' ? 'a club admin' : 'a registrar'}. {CLUB_ROLE_DESCRIPTIONS[inv.role]}
        </>
      }
    >
      {inv.expired ? (
        <Alert tone="warn">This invite has expired. Ask {inv.invitedBy} to send you a new one.</Alert>
      ) : user && user.email === inv.email ? (
        <div className="space-y-3">
          {accept.isError && <Alert>{errorMessage(accept.error)}</Alert>}
          <Button loading={accept.isPending} onClick={() => accept.mutate()} className="btn-primary btn-lg w-full">
            Accept and open {inv.clubName}
          </Button>
        </div>
      ) : user ? (
        <div className="space-y-4">
          <Alert tone="warn">
            You’re signed in as {user.email}, but this invite is for {inv.email}. Sign out, then sign in or create an account with {inv.email}.
          </Alert>
          <Button variant="secondary" loading={signOut.isPending} onClick={() => signOut.mutate()} className="btn-secondary w-full">
            Sign out
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <Link to={`/signup?email=${encodeURIComponent(inv.email)}&next=${encodeURIComponent(here)}`} className="btn-primary btn-lg w-full">
            Create an account
          </Link>
          <Link to={`/login?email=${encodeURIComponent(inv.email)}&next=${encodeURIComponent(here)}`} className="btn-secondary w-full">
            I already have an account
          </Link>
          <p className="text-center text-[13px] text-muted">Then come back to this link to accept.</p>
        </div>
      )}
    </AuthLayout>
  );
}

/** New to Hoopify: make an account and register the club in one go. */
export function ClubSignupPage() {
  const { data: session } = useSession();
  const [v, setV] = useState({ name: '', email: '', password: '', clubName: '', mobile: '', suburb: '', state: '', website: '' });
  const signup = useMutation({ mutationFn: () => api.post<{ status: 'check_email' }>('/auth/signup-club', { ...v, state: v.state || null }) });
  const availability = useAvailability();
  if (session?.status === 'signed_in') return <Navigate to="/clubs/register" replace />;
  const f = fieldErrors(signup.error);
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });

  if (availability.data && !availability.data.signups) {
    return (
      <AuthLayout title="Opening soon" description="Hoopify isn’t taking sign-ups just yet. Check back soon.">
        <Link to="/login" className="btn-secondary w-full">
          Sign in
        </Link>
      </AuthLayout>
    );
  }
  if (signup.data?.status === 'check_email') {
    return (
      <AuthLayout
        title="Check your email"
        description={
          <>
            We sent a link to <strong className="font-semibold text-ink">{v.email}</strong>. Open it to confirm your address.
          </>
        }
        footer={
          <>
            Nothing after a few minutes? Check your junk folder, or{' '}
            <button className="font-medium text-action hover:underline" onClick={() => signup.reset()}>
              try a different address
            </button>
            .
          </>
        }
      >
        <ol className="mb-6 list-decimal space-y-1.5 pl-5 text-[14px] text-muted">
          <li>Confirm your email with the link we sent.</li>
          <li>Hoopify calls you on {v.mobile || 'your mobile'} to check it’s your club, usually within a day or two.</li>
          <li>Once {v.clubName || 'your club'} is approved, sign in to set up its teams.</li>
        </ol>
        <Link to={`/login?email=${encodeURIComponent(v.email)}&next=${encodeURIComponent('/clubs')}`} className="btn-secondary w-full">
          Go to sign in
        </Link>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout
      title="Register your club"
      description="Set up all your club’s teams, coaches and players in one place. Hoopify checks each club with a quick phone call before it’s switched on."
      footer={
        <>
          Already have a Hoopify account?{' '}
          <Link to={`/login?next=${encodeURIComponent('/clubs/register')}`} className="font-medium text-action hover:underline">
            Sign in to register your club
          </Link>
          <span className="mt-2 block">
            Just coaching one team?{' '}
            <Link to="/signup" className="font-medium text-action hover:underline">
              Sign up as a coach
            </Link>
          </span>
        </>
      }
    >
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); signup.mutate(); }} className="space-y-4" noValidate>
        {signup.isError && !Object.keys(f).length && <Alert>{errorMessage(signup.error)}</Alert>}
        <p className="label-caps text-muted">Your club</p>
        <Field label="Club name" value={v.clubName} onChange={set('clubName')} error={f.clubName} placeholder="e.g. Knox Pumas Basketball Club" autoFocus />
        <div className="grid grid-cols-[1fr_auto] gap-4">
          <Field label="Suburb (optional)" value={v.suburb} onChange={set('suburb')} error={f.suburb} />
          <StateSelect value={v.state} onChange={(s) => setV({ ...v, state: s })} />
        </div>
        <Field label="Club website (optional)" value={v.website} onChange={set('website')} error={f.website} placeholder="e.g. knoxpumas.com.au" />
        <p className="label-caps border-t border-line pt-5 text-muted">You, the club admin</p>
        <Field label="Your name" autoComplete="name" value={v.name} onChange={set('name')} error={f.name} />
        <Field label="Email" type="email" autoComplete="email" value={v.email} onChange={set('email')} error={f.email} />
        <Field
          label="Mobile"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={v.mobile}
          onChange={set('mobile')}
          error={f.mobile}
          placeholder="0412 345 678"
          hint="We’ll call you on this to check it’s your club. It isn’t shown to anyone else."
        />
        <NewPasswordField value={v.password} onChange={(p) => setV({ ...v, password: p })} error={f.password} userInputs={[v.name, v.email]} />
        <Button type="submit" loading={signup.isPending} className="btn-primary btn-lg w-full">
          Create account and register club
        </Button>
      </form>
    </AuthLayout>
  );
}
